import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { calcDatePreset } from '../../components/fields/DateRangePicker';
import DateRangeSelect from '../../components/fields/DateRangeSelect';
import PaginationComponent from '../../components/PaginationComponent';
import QcPassRateCell from './QcPassRateCell';
import { qcResultAPI } from '../../services';
import { SOURCE_KIND_LABELS } from './QcResultsListTab';

const PAGE_SIZE = 20;

const SESSION_STATUS_LABELS = {
  // OPEN chỉ còn ở phiên thủ công admin tạo mà chưa ai nhận — phiên tự động vào thẳng IN_PROGRESS
  OPEN: 'Chờ nhận',
  IN_PROGRESS: 'Đang chấm',
  COMPLETED: 'Đã xong',
};

/**
 * #180900 — nhãn "đủ set đánh giá". Sếp: "Yêu cầu phải đủ 20 mới tạo thành một set
 * để dùng cho đánh giá được". Ngưỡng do server trả về (`min_items_for_evaluation`),
 * KHÔNG ghi cứng số 20 ở đây.
 */
export function EvaluationSetBadge({ row }) {
  if (!row) return null;
  const nguong = row.min_items_for_evaluation;
  if (row.counts_for_evaluation) {
    return (
      <Badge bg="primary" title={`Phiên đã đóng và đủ ${nguong} mục đã chấm — được tính vào đánh giá nhân viên`}>
        Đủ set
      </Badge>
    );
  }
  return (
    <Badge
      bg="secondary"
      title={
        row.session?.status === 'COMPLETED'
          ? `Phiên chỉ chấm được ${row.graded_items}/${nguong} mục nên không tính vào đánh giá nhân viên. Kết quả từng mục vẫn xem và xử lý bình thường.`
          : 'Phiên chưa đóng nên chưa được tính vào đánh giá nhân viên.'
      }
    >
      Chưa đủ set {row.graded_items}/{nguong}
    </Badge>
  );
}

/**
 * #131522 — đổi số giây thời gian kiểm sang chữ người đọc hiểu ngay.
 * Con số này do máy chủ đo, ĐÃ TRỪ quãng nhân viên không thao tác (ngưỡng 10 giây),
 * nên nó KHÔNG bằng khoảng cách từ lúc mở phiên tới lúc nộp bài.
 */
export function dinhDangThoiGianKiem(giay) {
  const so = Number(giay);
  if (!Number.isFinite(so) || so <= 0) return null;
  const phut = Math.floor(so / 60);
  const giayLe = Math.round(so % 60);
  if (phut === 0) return `${giayLe} giây`;
  const gio = Math.floor(phut / 60);
  if (gio === 0) return giayLe > 0 ? `${phut} phút ${giayLe} giây` : `${phut} phút`;
  const phutLe = phut % 60;
  return phutLe > 0 ? `${gio} giờ ${phutLe} phút` : `${gio} giờ`;
}

const SESSION_STATUS_VARIANTS = {
  OPEN: 'secondary',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
};

// Tổng hợp kết quả QC theo từng phiên — bổ sung cho tab "Theo nhân viên",
// trả lời câu hỏi "phiên nào chấm ra nhiều lỗi / còn treo chưa xử lý".
export default function QcResultsBySessionTab({ branchId, onSelectSession }) {
  const navigate = useNavigate();
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [sortBy, setSortBy] = useState('planned_task_date');
  const [status, setStatus] = useState('');
  // #180900 — '' = xem tất cả, 'true' = chỉ phiên đủ set đánh giá
  const [evaluationOnly, setEvaluationOnly] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await qcResultAPI.getQcResultsBySession({
        branch_id: branchId || undefined,
        start_date: range.from,
        end_date: range.to,
        status: status || undefined,
        evaluation_only: evaluationOnly || undefined,
        sort_by: sortBy,
        page,
        limit: PAGE_SIZE,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta || { total: 0 });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không tải được thống kê theo phiên');
    } finally {
      setLoading(false);
    }
  }, [branchId, range, status, evaluationOnly, sortBy, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [branchId, range, status, evaluationOnly, sortBy]);

  return (
    <>
      <Card className="qc-card mb-3">
        <Card.Body>
          <Row className="g-3">
            {/* #175019 — bỏ ô chọn chi nhánh riêng: chi nhánh chỉ chọn ở thanh trên đầu. */}
            <Col md={2}>
              <Form.Label>Trạng thái phiên</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Tất cả</option>
                <option value="IN_PROGRESS">Đang chấm</option>
                <option value="COMPLETED">Đã xong</option>
                <option value="OPEN">Chờ nhận (thủ công)</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Dùng cho đánh giá</Form.Label>
              <Form.Select value={evaluationOnly} onChange={(e) => setEvaluationOnly(e.target.value)}>
                <option value="">Tất cả phiên</option>
                <option value="true">Chỉ phiên đủ set đánh giá</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Sắp xếp theo</Form.Label>
              <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="planned_task_date">Ngày phiên (mới nhất)</option>
                <option value="failed">Số lỗi (nhiều nhất)</option>
                <option value="unhandled_failed">Chưa xử lý (nhiều nhất)</option>
                <option value="pass_rate">Tỉ lệ đạt (thấp nhất)</option>
                <option value="total">Tổng số (nhiều nhất)</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <DateRangeSelect fromDate={range.from} toDate={range.to} onChange={(from, to) => setRange({ from, to })} defaultPreset="last3months" />
              <Form.Text className="text-muted">Lọc theo ngày tạo phiên</Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có phiên QC nào theo bộ lọc hiện tại.</div>
      ) : (
        <>
          {meta.incomplete_sessions > 0 && (
            <div className="alert alert-warning py-2 small">
              <strong>{meta.incomplete_sessions}</strong> phiên trong danh sách chưa đủ{' '}
              <strong>{meta.min_items_for_evaluation}</strong> mục đã chấm nên{' '}
              <strong>không được tính vào đánh giá nhân viên</strong>. Kết quả từng mục vẫn xem và
              xử lý bình thường.
            </div>
          )}
          <div className="table-responsive qc-table-wrap">
            <Table hover size="sm" className="qc-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Ngày phiên</th>
                  <th>Chi nhánh</th>
                  <th>Người QC</th>
                  <th>Trạng thái</th>
                  <th className="text-end">Tổng</th>
                  <th className="text-end">Đạt</th>
                  <th className="text-end">Không đạt</th>
                  <th className="text-end">Chờ</th>
                  <th title="Đạt / (Đạt + Không đạt) — mục còn Chờ chưa chấm không nằm trong phép tính">
                    Tỉ lệ đạt <span className="text-muted fw-normal">(trên số đã chấm)</span>
                  </th>
                  <th>Chưa xử lý</th>
                  <th title="Thời gian nhân viên THỰC SỰ thao tác trong phiên, tính từ lúc mở phiên tới lúc nộp bài và đã trừ những quãng nghỉ không tương tác (quá 10 giây không tương tác là ngừng tính, tương tác lại thì tính tiếp)">
                    Thời gian kiểm
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.session.id}>
                    <tr>
                      <td>{r.session.planned_task_date}</td>
                      <td>{r.branch?.name || '-'}</td>
                      <td>{r.qc_employee?.full_name || <span className="text-muted">-</span>}</td>
                      <td>
                        <div className="d-flex flex-column gap-1 align-items-start">
                          <Badge bg={SESSION_STATUS_VARIANTS[r.session.status] || 'secondary'}>
                            {SESSION_STATUS_LABELS[r.session.status] || r.session.status}
                          </Badge>
                          <EvaluationSetBadge row={r} />
                        </div>
                      </td>
                      <td className="text-end">{r.total}</td>
                      <td className="text-end text-success fw-semibold">{r.passed}</td>
                      <td className="text-end text-danger fw-semibold">{r.failed}</td>
                      <td className="text-end">{r.pending + r.in_review}</td>
                      <td style={{ minWidth: 120 }}>
                        <QcPassRateCell
                          passRate={r.pass_rate}
                          passed={r.passed}
                          failed={r.failed}
                          pending={r.pending + r.in_review}
                        />
                      </td>
                      <td>{r.unhandled_failed > 0 ? <span className="text-danger fw-semibold">{r.unhandled_failed}</span> : 0}</td>
                      <td>
                        {dinhDangThoiGianKiem(r.active_seconds) || (
                          <span className="text-muted" title="Phiên chưa ghi nhận được quãng thao tác nào">
                            -
                          </span>
                        )}
                      </td>
                      <td className="text-nowrap"><div className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary" onClick={() => setExpandedId(expandedId === r.session.id ? null : r.session.id)}>
                          {expandedId === r.session.id ? 'Ẩn' : 'Chi tiết'}
                        </Button>
                        <Button size="sm" variant="outline-primary" onClick={() => onSelectSession(r.session.id)}>
                          Xem lỗi
                        </Button>
                        <Button size="sm" variant="outline-dark" onClick={() => navigate(`/qc-sessions/${r.session.id}`)}>
                          Phiên
                        </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === r.session.id && (
                      <tr>
                        <td colSpan={12} className="bg-light">
                          <div className="d-flex flex-wrap gap-3 py-2">
                            {Object.keys(r.by_source || {}).length === 0 ? (
                              <span className="text-muted">Phiên này chưa có mục nào được chấm.</span>
                            ) : (
                              Object.entries(r.by_source).map(([kind, s]) => (
                                <div key={kind}>
                                  <strong>{SOURCE_KIND_LABELS[kind] || kind}:</strong>{' '}
                                  <span className="text-success">{s.passed} đạt</span> / <span className="text-danger">{s.failed} không đạt</span> / {s.total} tổng
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
          <PaginationComponent totalItems={meta.total || 0} itemsPerPage={PAGE_SIZE} currentPage={page} onPageChange={setPage} loading={loading} />
        </>
      )}
    </>
  );
}
